import { registerForPushNotificationsAsync } from "@/utils/notification-handler";
import { clearFeedCache } from "@/utils/feedCache";
import { clearPostCache } from "@/utils/postCache";
import { UserDocument } from "@/types/user";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { getApp } from "firebase/app";
import {
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import type { User as WebUser } from "firebase/auth";
import {
  doc,
  getFirestore,
  onSnapshot,
} from "firebase/firestore";

// Type-only import: stripped from the runtime bundle by TS/babel, so the
// native RNFB package never actually resolves on web (metro.config.js also
// blocks it as a safety net). Keeps the exported `User` type structurally
// identical between AuthContext.web.tsx and AuthContext.tsx so native call
// sites that cast to FirebaseAuthTypes.User still type-check under TS's
// moduleSuffixes resolution.
import type { FirebaseAuthTypes } from "@react-native-firebase/auth";

type User = WebUser | FirebaseAuthTypes.User;

const auth = {
  onAuthStateChanged: (callback: (user: WebUser | null) => void) => {
    return onAuthStateChanged(getAuth(), callback);
  },

  signOut: async () => {
    await signOut(getAuth());
  },

  get currentUser() {
    return getAuth().currentUser;
  },
};

// Subscribes to users/{uid}. The returned unsubscribe must be called when the
// auth user changes or the provider unmounts. Live updates mean any write to
// the user doc (profile edit, photo upload, admin tool) flows into the context
// automatically — no manual refresh needed.
const subscribeUserDoc = (
  uid: string,
  onChange: (data: UserDocument | null) => void,
): (() => void) => {
  const handleError = (err: unknown) => {
    console.error("Failed to subscribe to user doc", err);
  };
  return onSnapshot(
    doc(getFirestore(getApp()), "users", uid),
    (snap) => {
      onChange(snap.exists() ? (snap.data() as UserDocument) : null);
    },
    handleError,
  );
};


interface AuthContextType {
  user: User | null;
  userData: UserDocument | null;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userData: null,
  isLoading: true,
  logout: () => {},
});

export const AuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let userDocUnsub: (() => void) | null = null;

    // Complete any pending linkWithRedirect result from a previous page load.
    // If a redirect just returned, Firebase attaches the linked provider to
    // currentUser and onAuthStateChanged fires with the updated providerData
    // — the self-heal below writes it. This call is cheap: it resolves to
    // null when there was no redirect.
    getRedirectResult(getAuth()).catch((err) => {
      console.warn("getRedirectResult failed", err);
    });

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        registerForPushNotificationsAsync(user);
        // Fire-and-forget self-heal for users/{uid}.providers and
        // users/{uid}.phoneNumber.verified. Idempotent: each helper no-ops
        // when Firestore already matches Auth. Runs once per auth state
        // change. The phone-verified sync fixes users whose old phone-signin
        // path never wrote phoneNumber.verified — see the helper's doc
        // comment for the failure modes.
        void (async () => {
          try {
            const mod = await import("@/utils/accountLinking.web");
            await mod.syncProvidersToFirestore(user);
            await mod.syncPhoneVerifiedToFirestore(user);
          } catch (err) {
            console.warn("Failed to sync providers on auth state change", err);
          }
        })();
      }
      if (userDocUnsub) {
        userDocUnsub();
        userDocUnsub = null;
      }
      if (mounted) {
        setUser(user);
        setIsLoading(false);
        if (user?.uid) {
          userDocUnsub = subscribeUserDoc(user.uid, (data) => {
            if (mounted) setUserData(data);
          });
        } else {
          setUserData(null);
        }
      }
    });

    return () => {
      mounted = false;
      unsubscribeAuth();
      if (userDocUnsub) userDocUnsub();
    };
  }, []);

  const logout = useCallback(async () => {
    // Drop per-viewer state so a follow-up sign-in on the same session (e.g.
    // account switch) can't briefly paint the previous viewer's posts before
    // the fresh feed reloads. clearFeedCache is a web-only concern (native
    // stub is a no-op — see utils/feedCache.native.ts).
    clearPostCache();
    clearFeedCache();
    await auth.signOut();
  }, []);

  const authContextValue = useMemo(
    () => ({
      user,
      userData,
      isLoading,
      logout,
    }),
    [user, userData, isLoading, logout]
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
